import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { matchGpsToRoadRoute } from '../utils/roadMatchRoute';

const ROUTE_LOOKBACK_MS = 24 * 60 * 60 * 1000;
const MATCH_DEBOUNCE_MS = 2500;

function isRecent(iso, minutes = 15) {
  if (!iso) return false;
  const age = Date.now() - new Date(iso).getTime();
  return age >= 0 && age <= minutes * 60 * 1000;
}

function groupTracksByTrip(rows) {
  const trips = new Map();

  (rows || []).forEach((row) => {
    if (!row.trip_id || row.latitude == null || row.longitude == null) return;

    if (!trips.has(row.trip_id)) {
      trips.set(row.trip_id, {
        id: row.trip_id,
        sensorId: row.sensor_id,
        zoneId: row.zone_id,
        points: [],
      });
    }

    trips.get(row.trip_id).points.push({
      lat: Number(row.latitude),
      lng: Number(row.longitude),
      recordedAt: row.recorded_at,
    });
  });

  return Array.from(trips.values()).filter((trip) => trip.points.length >= 2);
}

export function useFleetTripRoutes(userId, sensors, isAuthenticated, { from = null, to = null } = {}) {
  const [tripRoutes, setTripRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const rawTripsRef = useRef([]);
  const matchCacheRef = useRef(new Map());
  const matchTimerRef = useRef(null);
  const matchGenRef = useRef(0);
  const hasTimeFilter = Boolean(from && to);

  const sensorNameById = useCallback(() => {
    const map = {};
    (sensors || []).forEach((s) => {
      map[s.id] = s.name;
    });
    return map;
  }, [sensors]);

  const applyRoadMatching = useCallback(async (trips) => {
    const generation = ++matchGenRef.current;
    const names = sensorNameById();

    const matched = await Promise.all(
      trips.map(async (trip) => {
        const lastAt = trip.points[trip.points.length - 1]?.recordedAt || '';
        const cacheKey = `${trip.id}:${trip.points.length}:${lastAt}`;
        const cached = matchCacheRef.current.get(cacheKey);
        if (cached) {
          return {
            ...trip,
            path: cached.path,
            roadMatched: cached.roadMatched,
            isLive: isRecent(lastAt, 15),
            sensorName: names[trip.sensorId] || 'Sensor',
          };
        }

        const { path, roadMatched } = await matchGpsToRoadRoute(trip.points);
        matchCacheRef.current.set(cacheKey, { path, roadMatched });

        return {
          ...trip,
          path,
          roadMatched,
          isLive: isRecent(lastAt, 15),
          sensorName: names[trip.sensorId] || 'Sensor',
        };
      })
    );

    if (generation === matchGenRef.current) {
      setTripRoutes(matched);
    }
  }, [sensorNameById]);

  const scheduleRoadMatching = useCallback((trips) => {
    rawTripsRef.current = trips;
    if (matchTimerRef.current) clearTimeout(matchTimerRef.current);

    matchTimerRef.current = setTimeout(() => {
      applyRoadMatching(rawTripsRef.current);
    }, MATCH_DEBOUNCE_MS);
  }, [applyRoadMatching]);

  const loadTripRoutes = useCallback(async () => {
    if (!userId) {
      setTripRoutes([]);
      return;
    }

    setLoadingRoutes(true);
    try {
      const since = from || new Date(Date.now() - ROUTE_LOOKBACK_MS).toISOString();
      const until = to || new Date().toISOString();
      const { data, error } = await supabase
        .from('gps_tracks')
        .select('trip_id, sensor_id, zone_id, latitude, longitude, recorded_at')
        .eq('user_id', userId)
        .not('trip_id', 'is', null)
        .gte('recorded_at', since)
        .lte('recorded_at', until)
        .order('recorded_at', { ascending: true })
        .limit(2000);

      if (error) throw error;

      const trips = groupTracksByTrip(data);
      scheduleRoadMatching(trips);
    } catch (err) {
      console.error('useFleetTripRoutes: failed to load routes', err);
      setTripRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  }, [userId, scheduleRoadMatching, from, to]);

  const appendTrackPoint = useCallback((row) => {
    if (!row?.trip_id || row.latitude == null || row.longitude == null) return;

    const trips = [...rawTripsRef.current];
    const index = trips.findIndex((t) => t.id === row.trip_id);
    const point = {
      lat: Number(row.latitude),
      lng: Number(row.longitude),
      recordedAt: row.recorded_at,
    };

    if (index === -1) {
      trips.push({
        id: row.trip_id,
        sensorId: row.sensor_id,
        zoneId: row.zone_id,
        points: [point],
      });
    } else {
      trips[index] = {
        ...trips[index],
        points: [...trips[index].points, point],
      };
    }

    const viable = trips.filter((t) => t.points.length >= 2);
    scheduleRoadMatching(viable);
  }, [scheduleRoadMatching]);

  useEffect(() => {
    loadTripRoutes();
  }, [loadTripRoutes]);

  useEffect(() => {
    if (!isAuthenticated || !userId || hasTimeFilter) return undefined;

    const channel = supabase
      .channel(`fleet-trip-routes-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'gps_tracks',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => appendTrackPoint(payload.new)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (matchTimerRef.current) clearTimeout(matchTimerRef.current);
    };
  }, [isAuthenticated, userId, appendTrackPoint, hasTimeFilter]);

  return {
    tripRoutes,
    loadingRoutes,
    refreshRoutes: loadTripRoutes,
  };
}
